import type { UnstyledListTypeAndProps } from '@aurora/shared-client/components/common/List';
import { ListItemSpacing, ListVariant } from '@aurora/shared-client/components/common/List/enums';
import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import Pager from '@aurora/shared-client/components/common/Pager/Pager';
import type { PagerSeeAllModalTypeAndProps } from '@aurora/shared-client/components/common/Pager/types';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import PaginationHelper from '@aurora/shared-client/helpers/ui/PaginationHelper/PaginationHelper';
import { LoadingSize, ModalSize } from '@aurora/shared-client/types/enums';
import type { KudoConnection, User } from '@aurora/shared-generated/types/graphql-schema-types';
import {
  ConversationStyle,
  SortDirection
} from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { ItemType, UserViewVariant } from '../../../types/enums';
import type { KudosListQuery, KudosListQueryVariables } from '../../../types/graphql-types';
import ItemList from '../../common/List/ItemList';
import type { ItemViewTypeAndProps } from '../../entities/types';
import type { MessageTypes } from '../../messages/MessageView/types';
import type { UserViewInlineProps } from '../../users/UserView/types';
import useTranslation from '../../useTranslation';
import queryForModal from './KudosListPager.query.graphql';

interface Props {
  /**
   * Class name(s) to apply to the pager component.
   */
  className?: string;
  /**
   * The number of kudos for the message.
   */
  kudosSum: number;
  /**
   * The message id for the graphQL query.
   */
  messageId: string;
  /**
   * The title of the pager link.
   */
  linkTitle: React.FC<React.PropsWithChildren<unknown>>;
  /**
   * The message __typename.
   */
  messageType: MessageTypes;
  /**
   * The message conversation style.
   */
  conversationStyle?: ConversationStyle;
  /**
   * If the message is a root message or not
   */
  isRootMessage?: boolean;
}

/**
 * Creates a modal pager for a message kudoers.
 *
 * @constructor
 *
 * @author Patricio Poratto
 */
const KudosListPager: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  kudosSum,
  linkTitle: LinkTitle,
  messageId,
  messageType,
  conversationStyle,
  isRootMessage
}) => {
  const cx = useClassNameMapper();
  const { formatMessage, loading: textLoading } = useTranslation(EndUserComponent.KUDOS_LIST_PAGER);

  const kudosPageSize = 25;
  const viewProps: UserViewInlineProps = {
    useRank: true,
    useJoinDate: false
  };
  const variables = {
    messageId: messageId,
    first: kudosPageSize,
    sorts: {
      time: {
        direction: SortDirection.Desc
      }
    },
    ...viewProps
  } as KudosListQueryVariables;

  const { fetchMore } = useQueryWithTracing<KudosListQuery, KudosListQueryVariables>(
    module,
    queryForModal,
    {
      variables,
      skip: true
    }
  );

  if (textLoading) {
    return null;
  }

  const modalListVariantUnstyled: UnstyledListTypeAndProps<User> = {
    type: ListVariant.UNSTYLED,
    props: {
      listItemSpacing: ListItemSpacing.LG
    }
  };

  const modalViewVariantInline: ItemViewTypeAndProps<ItemType.USER, UserViewVariant.INLINE> = {
    type: UserViewVariant.INLINE,
    props: viewProps
  };

  const ModalTitle: React.FC<React.PropsWithChildren<unknown>> = () => (
    <span className={cx('text-body font-weight-bold')}>
      {formatMessage(
        conversationStyle === ConversationStyle.Idea && isRootMessage
          ? 'votesModalTitle'
          : 'likesModalTitle',
        { count: kudosSum }
      )}
    </span>
  );

  const pagerVariant: PagerSeeAllModalTypeAndProps<KudosListQuery, KudosListQueryVariables> = {
    type: PagerVariant.SEE_ALL_MODAL,
    props: {
      modalClassName: 'mb-0 pb-4',
      modalTitle: ModalTitle,
      modalSize: ModalSize.SM,
      queryAndOptions: {
        query: queryForModal,
        options: {
          variables
        }
      },
      // eslint-disable-next-line react/display-name
      modalContent: (modalQueryResult, modalBodyReference) => {
        return (
          <ItemList<
            User,
            ItemType.USER,
            ItemViewTypeAndProps<ItemType.USER, UserViewVariant>,
            KudosListQuery,
            KudosListQueryVariables
          >
            type={ItemType.USER}
            variant={modalViewVariantInline}
            queryResult={modalQueryResult}
            itemPath="message.kudos"
            onUpdate={(kudoConnection: KudoConnection): KudosListQuery => {
              return {
                message: {
                  id: messageId,
                  kudos: kudoConnection,
                  __typename: messageType
                }
              };
            }}
            listVariant={modalListVariantUnstyled}
            loadingSize={LoadingSize.SM}
            pageSize={kudosPageSize}
            pagerVariant={{
              type: PagerVariant.INFINITE_SCROLL,
              props: { rootReference: modalBodyReference }
            }}
            querySubObject="user"
          />
        );
      }
    }
  };

  const pageInfo = { hasNextPage: true };

  const paginationHelper = new PaginationHelper<KudosListQuery, KudosListQueryVariables>(
    fetchMore,
    pageInfo,
    'kudos',
    kudosPageSize,
    variables
  );

  return (
    <Pager
      className={cx(className)}
      loadPage={paginationHelper.loadPage}
      pageInfo={paginationHelper.pageInfo}
      variant={pagerVariant}
    >
      <LinkTitle />
    </Pager>
  );
};

export default KudosListPager;
